import React, { useEffect, useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Bell, Calendar, Mail, X } from "lucide-react";
import { api } from "../lib/api";

export default function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [notifs, setNotifs] = useState({ reservations: [], contacts: [], total: 0 });
  const navigate = useNavigate();
  const ref = useRef(null);

  const load = () => {
    api.get("/admin/notifications").then((r) => setNotifs(r.data)).catch(() => {});
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30000); // refresh every 30s
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const total = notifs.total;
  const hasNew = total > 0;

  const goToReservation = (id) => {
    setOpen(false);
    navigate(`/admin/reservations?highlight=${id}`);
    api.post(`/admin/reservations/${id}/mark-seen`).then(load).catch(() => {});
  };

  const goToContact = () => {
    setOpen(false);
    navigate("/admin/contacts");
  };

  const markAllSeen = async () => {
    await api.post("/admin/reservations/mark-all-seen").catch(() => {});
    load();
  };

  return (
    <div className="relative" ref={ref}>
      <button
        onClick={() => setOpen(!open)}
        data-testid="notif-bell"
        aria-label={`${total} nouvelle(s) notification(s)`}
        className={`relative w-10 h-10 rounded-full flex items-center justify-center transition-all ${
          hasNew
            ? "bg-fegreen text-white shadow-md hover:bg-fegreen/90 ring-2 ring-fegreen/30"
            : "bg-gray-100 text-fegray hover:bg-gray-200"
        }`}
      >
        <Bell className={`w-5 h-5 ${hasNew ? "animate-bell-shake" : ""}`} />
        {hasNew && (
          <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-[10px] font-extrabold rounded-full flex items-center justify-center border-2 border-white">
            {total > 9 ? "9+" : total}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 mt-3 w-96 max-w-[calc(100vw-2rem)] bg-white rounded-2xl shadow-2xl border border-gray-100 z-50 overflow-hidden animate-fade-in" data-testid="notif-dropdown">
          <div className="flex items-center justify-between p-4 border-b border-gray-100">
            <div>
              <h3 className="font-bold text-fegray-dark text-sm">Notifications</h3>
              <p className="text-xs text-fegray">{total > 0 ? `${total} nouvelle(s)` : "Aucune nouvelle"}</p>
            </div>
            {hasNew && (
              <button
                onClick={markAllSeen}
                data-testid="notif-mark-all"
                className="text-xs text-fegreen hover:underline font-semibold"
              >
                Tout marquer lu
              </button>
            )}
            <button onClick={() => setOpen(false)} className="w-7 h-7 rounded-full hover:bg-gray-100 flex items-center justify-center">
              <X className="w-3.5 h-3.5 text-fegray" />
            </button>
          </div>

          <div className="max-h-[60vh] overflow-y-auto">
            {/* Reservations */}
            {notifs.reservations.length > 0 && (
              <div>
                <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-wider text-fegreen font-bold flex items-center gap-1.5">
                  <Calendar className="w-3 h-3" /> Réservations payées
                </div>
                {notifs.reservations.map((r) => (
                  <button
                    key={r.id}
                    onClick={() => goToReservation(r.id)}
                    data-testid={`notif-res-${r.id}`}
                    className="w-full text-left p-4 hover:bg-fegreen-light/30 transition-colors border-b border-gray-50 last:border-0 flex items-start gap-3"
                  >
                    <div className="w-9 h-9 rounded-full bg-fegreen text-white flex items-center justify-center shrink-0">
                      <Calendar className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-fegray-dark text-sm truncate">
                        {r.prenom} {r.nom}
                      </div>
                      <div className="text-xs text-fegray truncate">
                        {r.pack_name} · {(r.amount_after_credit || r.amount || 0).toFixed(2)}€
                      </div>
                      <div className="text-[10px] text-fegreen font-semibold mt-1 uppercase tracking-wider">
                        ✨ Nouvelle réservation payée
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {/* Contacts */}
            {notifs.contacts.length > 0 && (
              <div>
                <div className="px-4 pt-3 pb-1 text-[10px] uppercase tracking-wider text-amber-600 font-bold flex items-center gap-1.5">
                  <Mail className="w-3 h-3" /> Nouveaux messages
                </div>
                {notifs.contacts.map((c) => (
                  <button
                    key={c.id}
                    onClick={goToContact}
                    data-testid={`notif-contact-${c.id}`}
                    className="w-full text-left p-4 hover:bg-amber-50 transition-colors border-b border-gray-50 last:border-0 flex items-start gap-3"
                  >
                    <div className="w-9 h-9 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                      <Mail className="w-4 h-4" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-fegray-dark text-sm truncate">
                        {c.prenom} {c.nom}
                      </div>
                      <div className="text-xs text-fegray truncate">{c.objet || c.email}</div>
                      <div className="text-[10px] text-amber-600 font-semibold mt-1 uppercase tracking-wider">
                        Nouveau message
                      </div>
                    </div>
                  </button>
                ))}
              </div>
            )}

            {total === 0 && (
              <div className="text-center p-10 text-fegray text-sm">
                <Bell className="w-7 h-7 mx-auto mb-2 text-fegray/40" />
                Tout est à jour ✨
              </div>
            )}
          </div>

          {total > 0 ? (
            <Link
              to="/admin/reservations"
              onClick={() => setOpen(false)}
              className="block text-center text-sm text-fegreen font-semibold p-3 border-t border-gray-100 hover:bg-fegreen-light/30 transition-colors"
              data-testid="notif-see-all"
            >
              Voir toutes les réservations →
            </Link>
          ) : null}

          <Link
            to="/admin/notifications"
            onClick={() => setOpen(false)}
            className="block text-center text-xs text-fegray hover:text-fegreen font-semibold p-3 border-t border-gray-100 hover:bg-fegray-light/60 transition-colors uppercase tracking-wider"
            data-testid="notif-history-link"
          >
            Historique complet des notifications
          </Link>
        </div>
      )}
    </div>
  );
}
